/**
 * Diagram-template: "Risk and Security Assessment" / "Risico- en
 * veiligheidsevaluatie".
 *
 * Vaste pre-gevulde inhoud voor het uitwerken van een R&S-assessment.
 * Geometry + bilingual labels + opmaak zijn gekopieerd uit de
 * AMEFF-werkversie (`5f7b1381-e17c-4384-8e9c-b516bb7a9b1a`) op
 * 2026-05-27.
 *
 * Layout:
 *  - Centrum: 1 grote ronde "Aanpak / Approach" + 8 kleine
 *    gekleurde ronde drawing.Rectangles (88×88 met
 *    `borderRadiusOverride: "50%"`) als topic-labels
 *  - Buitenring: 8 drawing.ListBoxes (één per topic), leeg bij
 *    instantiatie zodat de modelleur ze zelf vult vanuit Explorer
 *  - View-kind: `risk.security-overview` (= bestaand type,
 *    auto_apply_stereotype risk.Asset blijft werken)
 *
 * Gebruik via "Nieuw diagram"-dropdown in de tabbar (createFromTemplate
 * in workspace-store).
 */

import type { BilingualText, NodeStyle } from "@ae/domain";

export interface TemplateRectangleSpec {
  /** Concept-typeId — drawing.Rectangle of drawing.ListBox. */
  typeId: "drawing.Rectangle" | "drawing.ListBox";
  /** Bilingual concept-naam. ListBox toont deze als titel boven de lijst. */
  name: BilingualText;
  /** Optionele bilingual beschrijving. */
  description?: BilingualText;
  /** Linkerboven-coord op het canvas. */
  position: { x: number; y: number };
  /** Pixel-maten. */
  width: number;
  height: number;
  /** Visuele styling — fill/stroke/font/border-radius. */
  nodeStyle: NodeStyle;
}

export interface DiagramTemplate {
  /** Stable id om de template via een dropdown te identificeren. */
  id: string;
  /** Bilingual label voor de "Nieuw"-dropdown. */
  label: BilingualText;
  /** Default-naam voor het resulterende diagram. */
  defaultDiagramName: BilingualText;
  /** View-kind van het nieuwe diagram. */
  viewKind: string;
  /** Lijst van te creëren concepten + viewState-entries. */
  elements: TemplateRectangleSpec[];
}

// ── Kleuren-palet (gekopieerd uit de werkversie) ─────────────────────
const COLOR_RED = "#bf0000";
const COLOR_GREEN = "#008000";
const COLOR_YELLOW = "#ffff00";
const COLOR_WHITE = "#ffffff";
const COLOR_BLACK = "#000000";

const CIRCLE_STYLE_RED: NodeStyle = {
  fillColor: COLOR_RED,
  strokeColor: "transparent",
  fontColor: COLOR_WHITE,
  fontSize: 10,
  fontFamily: "Arial",
  bold: true,
  borderRadiusOverride: "50%",
};
const CIRCLE_STYLE_GREEN: NodeStyle = {
  fillColor: COLOR_GREEN,
  strokeColor: "transparent",
  fontColor: COLOR_WHITE,
  fontSize: 10,
  fontFamily: "Arial",
  bold: true,
  borderRadiusOverride: "50%",
};
const CIRCLE_STYLE_YELLOW: NodeStyle = {
  fillColor: COLOR_YELLOW,
  strokeColor: "transparent",
  fontColor: COLOR_BLACK,
  fontSize: 10,
  fontFamily: "Arial",
  bold: true,
  borderRadiusOverride: "50%",
};
const APPROACH_CIRCLE_STYLE: NodeStyle = {
  fillColor: "transparent",
  strokeColor: "transparent",
  fontColor: COLOR_BLACK,
  fontSize: 24,
  fontFamily: "Arial",
  bold: true,
  borderRadiusOverride: "50%",
};

const LISTBOX_STYLE_RED: NodeStyle = {
  fillColor: COLOR_RED,
  strokeColor: "transparent",
  fontColor: COLOR_WHITE,
  fontSize: 10,
  fontFamily: "Arial",
  bold: true,
};
const LISTBOX_STYLE_GREEN: NodeStyle = {
  fillColor: COLOR_GREEN,
  strokeColor: "transparent",
  fontColor: COLOR_WHITE,
  fontSize: 10,
  fontFamily: "Arial",
  bold: true,
};
const LISTBOX_STYLE_YELLOW: NodeStyle = {
  fillColor: COLOR_YELLOW,
  strokeColor: "transparent",
  fontColor: COLOR_BLACK,
  fontSize: 10,
  fontFamily: "Arial",
  bold: true,
};

export const RISK_SECURITY_ASSESSMENT_TEMPLATE: DiagramTemplate = {
  id: "risk.security-assessment-template",
  label: {
    nl: "Risico- en veiligheidsevaluatie",
    en: "Risk and Security Assessment",
  },
  defaultDiagramName: {
    nl: "Risico- en veiligheidsevaluatie",
    en: "Risk and Security Assessment",
  },
  viewKind: "risk.security-overview",
  elements: [
    // ── Centrum: grote "Aanpak / Approach"-cirkel ─────────────────────
    {
      typeId: "drawing.Rectangle",
      name: { nl: "Aanpak", en: "Approach" },
      description: {
        nl: "Centrale aanpak voor de risico- en veiligheidsevaluatie. Beschrijf hier op hoofdlijnen de methode en scope.",
        en: "Central approach for the risk and security assessment. Describe the high-level method and scope here.",
      },
      position: { x: 67, y: -68 },
      width: 189,
      height: 189,
      nodeStyle: APPROACH_CIRCLE_STYLE,
    },

    // ── 8 topic-circles in een ring rond de center ────────────────────
    {
      typeId: "drawing.Rectangle",
      name: { nl: "Risico's", en: "Risks" },
      description: {
        nl: "Geïdentificeerde risico's die voortvloeien uit dreigingen die zwakheden uitbuiten en activa raken.",
        en: "Identified risks resulting from threats exploiting vulnerabilities and affecting assets.",
      },
      position: { x: 65, y: -145 },
      width: 88,
      height: 88,
      nodeStyle: CIRCLE_STYLE_RED,
    },
    {
      typeId: "drawing.Rectangle",
      name: { nl: "Bedreigingen", en: "Threats" },
      description: {
        nl: "Potentiële gebeurtenissen of dreiging-actoren die schade aan activa kunnen veroorzaken.",
        en: "Potential events or threat actors that could cause damage to assets.",
      },
      position: { x: -10, y: -69 },
      width: 88,
      height: 88,
      nodeStyle: CIRCLE_STYLE_RED,
    },
    {
      typeId: "drawing.Rectangle",
      name: { nl: "Kwetsbaarheden", en: "Vulnerabilities" },
      description: {
        nl: "Zwakheden in mensen, processen, technologie of fysieke beveiliging die door dreigingen kunnen worden misbruikt.",
        en: "Weaknesses in people, processes, technology, or physical security that threats can exploit.",
      },
      position: { x: -10, y: 34 },
      width: 88,
      height: 88,
      nodeStyle: CIRCLE_STYLE_RED,
    },
    {
      typeId: "drawing.Rectangle",
      name: { nl: "Activa", en: "Assets" },
      description: {
        nl: "Waardevolle middelen — data, applicaties, infrastructuur, mensen — die beschermd moeten worden.",
        en: "Valuable resources — data, applications, infrastructure, people — that need to be protected.",
      },
      position: { x: 65, y: 110 },
      width: 88,
      height: 88,
      nodeStyle: CIRCLE_STYLE_YELLOW,
    },
    {
      typeId: "drawing.Rectangle",
      name: {
        nl: "Beveiligingsbeleid & Principes",
        en: "Security Policies & Principles",
      },
      description: {
        nl: "Overkoepelende beleidslijnen en architectuurprincipes die de richting voor beveiliging bepalen.",
        en: "Overarching policies and architecture principles that guide the security direction.",
      },
      position: { x: 172, y: -144 },
      width: 88,
      height: 88,
      nodeStyle: CIRCLE_STYLE_GREEN,
    },
    {
      typeId: "drawing.Rectangle",
      name: {
        nl: "Doelstellingen voor controle",
        en: "Control Objectives",
      },
      description: {
        nl: "Doelstellingen waar controlemaatregelen aan moeten voldoen (bv. uit ISO/NIST/SOC2-frameworks).",
        en: "Objectives that control measures must satisfy (e.g. from ISO/NIST/SOC2 frameworks).",
      },
      position: { x: 246, y: -69 },
      width: 88,
      height: 88,
      nodeStyle: CIRCLE_STYLE_GREEN,
    },
    {
      typeId: "drawing.Rectangle",
      name: {
        nl: "Vereiste Controlemaatregelen",
        en: "Required Control Measures",
      },
      description: {
        nl: "Lijst van benodigde controlemaatregelen die de controle-doelstellingen realiseren.",
        en: "List of required control measures that realize the control objectives.",
      },
      position: { x: 246, y: 34 },
      width: 88,
      height: 88,
      nodeStyle: CIRCLE_STYLE_GREEN,
    },
    {
      typeId: "drawing.Rectangle",
      name: {
        nl: "Ontwerp van Controlemaatregelen",
        en: "Design of Control Measures",
      },
      description: {
        nl: "Concrete uitwerking en implementatie van de gekozen controlemaatregelen in mensen, processen en technologie.",
        en: "Concrete elaboration and implementation of the chosen control measures across people, processes, and technology.",
      },
      position: { x: 172, y: 110 },
      width: 88,
      height: 88,
      nodeStyle: CIRCLE_STYLE_GREEN,
    },

    // ── 8 ListBoxes — leeg, klaar voor drag-from-Explorer ─────────────
    {
      typeId: "drawing.ListBox",
      name: { nl: "Risico's", en: "Risks" },
      description: {
        nl: "Sleep risico-objecten uit de Explorer naar dit lijstvak om ze in scope te nemen.",
        en: "Drag risk objects from the Explorer into this list box to include them in scope.",
      },
      position: { x: -267, y: -200 },
      width: 240,
      height: 80,
      nodeStyle: LISTBOX_STYLE_RED,
    },
    {
      typeId: "drawing.ListBox",
      name: { nl: "Bedreigingen", en: "Threats" },
      description: {
        nl: "Sleep dreiging-objecten (Threat Events / Actors) uit de Explorer hierheen.",
        en: "Drag threat objects (Threat Events / Actors) from the Explorer here.",
      },
      position: { x: -267, y: -97 },
      width: 240,
      height: 80,
      nodeStyle: LISTBOX_STYLE_RED,
    },
    {
      typeId: "drawing.ListBox",
      name: { nl: "Kwetsbaarheden", en: "Vulnerabilities" },
      description: {
        nl: "Sleep kwetsbaarheid-objecten uit de Explorer hierheen.",
        en: "Drag vulnerability objects from the Explorer here.",
      },
      position: { x: -267, y: 8 },
      width: 240,
      height: 80,
      nodeStyle: LISTBOX_STYLE_RED,
    },
    {
      typeId: "drawing.ListBox",
      name: { nl: "Activa", en: "Assets" },
      description: {
        nl: "Sleep activa-objecten of -diagrammen uit de Explorer hierheen.",
        en: "Drag asset objects or diagrams from the Explorer here.",
      },
      position: { x: -267, y: 116 },
      width: 240,
      height: 163,
      nodeStyle: LISTBOX_STYLE_YELLOW,
    },
    {
      typeId: "drawing.ListBox",
      name: {
        nl: "Beveiligingsbeleid & Principes",
        en: "Security Policies & Principles",
      },
      description: {
        nl: "Sleep beleidsdocumenten en architectuurprincipes uit de Explorer hierheen.",
        en: "Drag policies and architecture principles from the Explorer here.",
      },
      position: { x: 347, y: -200 },
      width: 300,
      height: 80,
      nodeStyle: LISTBOX_STYLE_GREEN,
    },
    {
      typeId: "drawing.ListBox",
      name: {
        nl: "Doelstellingen voor controle",
        en: "Control Objectives",
      },
      description: {
        nl: "Sleep control-objective-doelstellingen hierheen vanuit de Explorer.",
        en: "Drag control objective goals into this list box from the Explorer.",
      },
      position: { x: 347, y: -97 },
      width: 300,
      height: 80,
      nodeStyle: LISTBOX_STYLE_GREEN,
    },
    {
      typeId: "drawing.ListBox",
      name: {
        nl: "Vereiste Controlemaatregelen",
        en: "Required Control Measures",
      },
      description: {
        nl: "Sleep benodigde controlemaatregelen uit de Explorer hierheen.",
        en: "Drag required control measures from the Explorer here.",
      },
      position: { x: 347, y: 8 },
      width: 300,
      height: 80,
      nodeStyle: LISTBOX_STYLE_GREEN,
    },
    {
      typeId: "drawing.ListBox",
      name: {
        nl: "Ontwerp van Controlemaatregelen",
        en: "Design of Control Measures",
      },
      description: {
        nl: "Sleep uitgewerkte ontwerp-diagrammen voor controlemaatregelen hierheen.",
        en: "Drag elaborated design diagrams for control measures here.",
      },
      position: { x: 347, y: 116 },
      width: 300,
      height: 163,
      nodeStyle: LISTBOX_STYLE_GREEN,
    },
  ],
};

/** Index voor lookups (verwacht 1 template — uitbreidbaar als er meer komen). */
export const DIAGRAM_TEMPLATES: DiagramTemplate[] = [RISK_SECURITY_ASSESSMENT_TEMPLATE];
